import React from 'react';

import { cn } from '../../../lib/cn';
import type { DestrezaVariant, SessionArchetype, SessionConfig } from '../../types/sessionArchetypes';
import {
  DESTREZA_VARIANT_OPTIONS,
  SESSION_ARCHETYPE_OPTIONS,
  defaultSessionConfig,
} from '../../types/sessionArchetypes';

interface SessionArchetypeSelectorProps {
  value?: SessionConfig;
  onChange: (config: SessionConfig) => void;
  disabled?: boolean;
  variant?: 'default' | 'compact';
  tone?: 'default' | 'swiss';
  helperText?: string | null;
}

export function SessionArchetypeSelector({
  value,
  onChange,
  disabled = false,
  variant = 'default',
  tone = 'default',
  helperText,
}: SessionArchetypeSelectorProps) {
  const selected = value ?? defaultSessionConfig('destreza');

  const selectArchetype = (archetype: SessionArchetype) => {
    if (disabled) return;
    onChange(defaultSessionConfig(archetype));
  };

  const selectVariant = (variantId: DestrezaVariant) => {
    if (disabled || selected.archetype !== 'destreza') return;
    onChange({ archetype: 'destreza', variant: variantId });
  };

  if (variant === 'compact') {
    const activeClass =
      tone === 'swiss'
        ? 'border-gray-900 bg-gray-900 text-white'
        : 'border-indigo-300 bg-indigo-100 text-indigo-800';
    const inactiveClass =
      tone === 'swiss'
        ? 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
        : 'border-[var(--sala-border-strong)] bg-[var(--sala-surface)] text-[var(--sala-ink-muted)]';
    const labelClass =
      tone === 'swiss' ? 'text-xs font-medium text-gray-500' : 'text-[10px] font-bold uppercase tracking-wide text-[var(--sala-ink-muted)]';

    return (
      <div className="space-y-2">
        <p className={labelClass}>Formato de sesión</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {SESSION_ARCHETYPE_OPTIONS.map((option) => {
            const active = selected.archetype === option.id;
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => selectArchetype(option.id)}
                title={option.description}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-[11px] font-medium transition',
                  active ? activeClass : inactiveClass,
                )}
              >
                {option.label}
              </button>
            );
          })}
          {selected.archetype === 'destreza'
            ? DESTREZA_VARIANT_OPTIONS.map((option) => {
                const active = (selected.variant ?? 'guiado') === option.id;
                const variantActiveClass =
                  tone === 'swiss'
                    ? 'border-gray-700 bg-gray-100 text-gray-900'
                    : 'border-violet-300 bg-violet-100 text-violet-800';
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => selectVariant(option.id)}
                    title={option.description}
                    className={cn(
                      'rounded-md border px-2.5 py-1 text-[11px] font-medium transition',
                      active ? variantActiveClass : inactiveClass,
                    )}
                  >
                    {option.label}
                  </button>
                );
              })
            : null}
        </div>
        {helperText ? (
          <p className="mt-2 text-xs leading-relaxed text-gray-500">💡 {helperText}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--sala-ink-muted)]">
          Formato de sesión
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {SESSION_ARCHETYPE_OPTIONS.map((option) => {
            const active = selected.archetype === option.id;
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => selectArchetype(option.id)}
                className="rounded-lg border px-3 py-2 text-left transition shadow-sm"
                style={{
                  borderColor: active ? 'var(--sala-accent)' : 'var(--sala-border-strong)',
                  background: active
                    ? 'color-mix(in srgb, var(--sala-accent) 12%, transparent)'
                    : 'var(--sala-surface)',
                }}
              >
                <p className="text-xs font-bold">{option.label}</p>
                <p className="mt-0.5 text-[10px] leading-snug text-[var(--sala-ink-muted)]">
                  {option.description}
                </p>
              </button>
            );
          })}
        </div>
        {helperText ? (
          <p className="mt-2 text-xs leading-relaxed text-[var(--sala-ink-muted)]">💡 {helperText}</p>
        ) : null}
      </div>

      {selected.archetype === 'destreza' ? (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--sala-ink-muted)]">
            Modalidad de destreza
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {DESTREZA_VARIANT_OPTIONS.map((option) => {
              const active = (selected.variant ?? 'guiado') === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => selectVariant(option.id)}
                  className="rounded-full border px-3 py-1.5 text-left text-[10px] font-semibold shadow-sm transition"
                  style={{
                    borderColor: active ? 'var(--sala-accent)' : 'var(--sala-border-strong)',
                    background: active
                      ? 'var(--sala-accent)'
                      : 'var(--sala-surface-raised)',
                    color: active ? 'var(--sala-on-accent, var(--sala-void))' : 'var(--sala-ink)',
                  }}
                  title={option.description}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[10px] text-[var(--sala-ink-muted)]">
            {(selected.variant ?? 'guiado') === 'guiado'
              ? 'Con modelado (práctica guiada)'
              : 'Práctica autónoma (ensayo / resolución)'}
          </p>
        </div>
      ) : null}
    </div>
  );
}